import { inject, Injectable, isDevMode } from '@angular/core';
import { StorageService } from '@gfc-flash/service-storage';
import { CachingRequestService } from '@gfc-flash/service-caching-request';
import {
  AuthentificationInitializerService,
  ServiceAuthentificationConfigInterface,
} from '@gfc-flash/service-authentification';
import { NativeOpenStorageOptionsInterface } from '@gfc-flash/plugin-native-storage-sqlite';
import { TranslateService } from '@gfc-flash/service-translate';
import { environment } from '@env/environment';
import { TextCaseTransformPipe } from '@gfc-flash/pipe-text-case-transform';
import { ErrorService } from '@gfc-flash/service-error';
import { TextCaseTransformEnum } from '@gfc-flash/package-text-case-transform';

/**
 * @description Boots cross-cutting services required before the routed application is used.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalServicesInitializerService {
  private readonly authentificationInitializerService: AuthentificationInitializerService =
    inject(AuthentificationInitializerService);
  private readonly cachingRequestService: CachingRequestService = inject(
    CachingRequestService,
  );
  private readonly errorService: ErrorService = inject(ErrorService);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly translateService: TranslateService =
    inject(TranslateService);

  private databaseStorage: NativeOpenStorageOptionsInterface =
    environment.databaseStorage;

  private databaseCachingRequest: NativeOpenStorageOptionsInterface =
    environment.databaseCachingRequest;

  private optionsAuthentification: ServiceAuthentificationConfigInterface =
    environment.optionsAuthentification;

  constructor() {}

  /**
   * @description Returns the storage prefix shared by application persistence services.
   */
  private getStoragePrefix(): string {
    return new TextCaseTransformPipe().transform(
      environment.appName,
      TextCaseTransformEnum.STORAGE_CASE,
    );
  }

  /**
   * @description Configures error handling, translations, persistence, request caching and authentication.
   */
  async initializeServices() {
    const storagePrefix = this.getStoragePrefix();

    this.errorService.initErrorService({
      isTesting: isDevMode(),
      updateAppConfiguration: {
        appStore: {
          appName: environment.appName,
          appId: environment.appId,
        },
        microsoftStore: {
          appId: environment.appId,
        },
        playStore: {
          appId: environment.appId,
        },
      },
    });
    this.translateService.initTranslate();

    await this.cachingRequestService.initCachingRequest({
      database: this.databaseCachingRequest,
      storagePrefix,
    });
    await this.storageService.initStorage({
      prefix: storagePrefix,
      database: this.databaseStorage,
    });
    await this.authentificationInitializerService.initAuthentificationService(
      this.optionsAuthentification,
    );
    await this.authentificationInitializerService.initializeSocialLoginNativePlugin();
  }

  /**
   * @description Returns the storage database configuration used during service initialization.
   */
  getDatabaseStorage() {
    return this.databaseStorage;
  }
}
